import React from 'react';
import { View } from 'react-native';

import Text from '../../components/text/Text';
import { useThemedStyles } from '../../hooks/useThemedStyles';

/**
 * ProgressBar: "4 of 8 done" style progress.
 * Props:
 * - done, total (numbers) or value (0..1)
 * - label (left, e.g. "4 of 8 done"), hint (right, e.g. "Then out on the run")
 * - variant: 'regular' (8 px rounded track with labels) | 'thin' (4 px flush bar under a header)
 * - style
 */
const ProgressBar = ({ done, total, value, label, hint, variant = 'regular', style }) => {
	const styles = useThemedStyles(createStyles);
	const ratio = value ?? (total > 0 ? done / total : 0);
	const clampedRatio = Math.min(Math.max(ratio || 0, 0), 1);
	const percent = Math.round(clampedRatio * 100);
	const isThin = variant === 'thin';

	return (
		<View style={[!isThin && styles.container, style]}>
			{!isThin && Boolean(label || hint) && (
				<View style={styles.labels}>
					{Boolean(label) && <Text style={styles.label}>{label}</Text>}
					{Boolean(hint) && <Text style={styles.hint}>{hint}</Text>}
				</View>
			)}
			<View
				style={[styles.track, isThin && styles.trackThin]}
				accessibilityRole="progressbar"
				accessibilityLabel={label}
				accessibilityValue={{ min: 0, max: 100, now: percent }}
			>
				<View style={[styles.fill, isThin && styles.fillThin, { width: `${percent}%` }]} />
			</View>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	container: {
		gap: 6,
	},
	labels: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		gap: space.md,
	},
	label: {
		...type.secondaryStrong,
		color: colors.text,
	},
	hint: {
		...type.secondary,
		color: colors.textSecondary,
		flexShrink: 1,
		textAlign: 'right',
	},
	track: {
		height: 8,
		borderRadius: 4,
		backgroundColor: colors.border,
		overflow: 'hidden',
	},
	trackThin: {
		height: 4,
		borderRadius: 0,
	},
	fill: {
		height: 8,
		borderRadius: 4,
		backgroundColor: colors.progress,
	},
	fillThin: {
		height: 4,
		borderRadius: 0,
	},
});

export default ProgressBar;
